import { generateUID } from "@/canv/utils";
import type { elementBase } from "@/elementBase/elementBase";
import { textWidget } from "@/elements/text/text";
import { TextWidgetV2 } from "@/elements/text/textV2";
import type { TextV2Data } from "@/elements/text/typesV2";
import type { Group } from "@/group";

export type TextPartEntityType = "textWidget" | "textWidgetV2";
export type TextPart = textWidget | TextWidgetV2;

const DEFAULT_TEXT_PART_ENTITY_TYPE: TextPartEntityType = "textWidgetV2";

export const resolveTextPartEntityType = (entityType: string): TextPartEntityType => {
    if (entityType === "textWidget") {
        return "textWidget";
    }

    return DEFAULT_TEXT_PART_ENTITY_TYPE;
};

export const resolveTextPartEntityTypeFromState = (textState: unknown): TextPartEntityType => {
    const entityType =
        typeof textState === "object" &&
        textState !== null &&
        "entityType" in textState &&
        typeof textState.entityType === "string"
            ? textState.entityType
            : DEFAULT_TEXT_PART_ENTITY_TYPE;

    return resolveTextPartEntityType(entityType);
};

export const createTextPartByEntityType = (
    textPartType: TextPartEntityType = DEFAULT_TEXT_PART_ENTITY_TYPE,
    startX = 0,
    startY = 0,
    data?: TextV2Data,
): TextPart => {
    if (textPartType === "textWidget") {
        return new textWidget(startX, startY);
    }

    return new TextWidgetV2(startX, startY, data);
};

export const isTextWidget = (textPart: TextPart): textPart is textWidget => {
    return textPart instanceof textWidget;
};

export const isTextWidgetV2 = (
    textPart: TextPart | elementBase | Group,
): textPart is TextWidgetV2 => {
    return textPart instanceof TextWidgetV2;
};

export const isTextWidgetV2Editing = (textPart: TextPart) => {
    return textPart instanceof TextWidgetV2 && textPart.getIsEditing();
};

export const regenerateTextPartUID = (textPart: TextPart, parentUID = "") => {
    textPart.UID = generateUID();
    textPart.parentUID = parentUID;
    textPart.trailUID = "";

    textPart.regenerateUIDS();
};

export const ensureTextPartByEntityType = (
    currentTextPart: TextPart,
    textPartType: TextPartEntityType,
    startX = 0,
    startY = 0,
): TextPart => {
    const needTextWidget = textPartType === "textWidget";

    if (
        (needTextWidget && isTextWidget(currentTextPart)) ||
        (!needTextWidget && !isTextWidget(currentTextPart))
    ) {
        return currentTextPart;
    }

    return createTextPartByEntityType(textPartType, startX, startY);
};
